// Wire schemas (§20.3, §20.4). Field order is normative. Each record is a table of fields, so the
// encoder, decoder, snapshot apply and the byte-size assertions share one definition.
import { MAP_TILE_COUNT, MAP_WEDGE_COUNT } from '../constants';
import { MapId, type BS, type EK, type ItemKind } from '../ids';
import type { Bean, Entity, ModeState } from '../types';
import type { BinReader, BinWriter } from './binary';

/** First byte of every binary frame. */
export const BinMsg = { INPUT: 1, SNAPSHOT: 2 } as const;
export type BinMsg = (typeof BinMsg)[keyof typeof BinMsg];

/** Meters ↔ centimeters (positions, velocities, event points). */
export const CM = 100;
/** Teacup tilt radians × 10000. */
export const TILT_SCALE = 10000;
/** Input axes ↔ int8. */
export const AXIS_SCALE = 127;

export type FieldType = 'u8' | 'u16' | 'i16' | 'ang8';
const FIELD_BYTES: Readonly<Record<FieldType, number>> = { u8: 1, u16: 2, i16: 2, ang8: 1 };

export interface FieldDef<T, K extends string> {
  readonly name: K;
  readonly type: FieldType;
  /** Wire value = round(value × scale); ignored for ang8. */
  readonly scale: number;
  get(src: T): number;
  set(dst: T, v: number): void;
}

export function writeField(w: BinWriter, type: FieldType, v: number, scale: number): void {
  switch (type) {
    case 'u8': w.u8(v * scale); break;
    case 'u16': w.u16(v * scale); break;
    case 'i16': w.q16(v, scale); break;
    case 'ang8': w.ang8(v); break;
  }
}

export function readField(r: BinReader, type: FieldType, scale: number): number {
  switch (type) {
    case 'u8': return r.u8() / scale;
    case 'u16': return r.u16() / scale;
    case 'i16': return r.q16(scale);
    case 'ang8': return r.ang8();
  }
}

export function recordBytes<T, K extends string>(schema: readonly FieldDef<T, K>[]): number {
  return schema.reduce((n, f) => n + FIELD_BYTES[f.type], 0);
}

// ---- BEAN record (49 B) ------------------------------------------------------------------------

/** `flags` bits of the BEAN record. */
export const BeanFlag = { OUT: 1, HAS_POTATO: 2, HOLDS_CROWN: 4, COYOTE: 8 } as const;

/** A decoded BEAN record: meters, m/s and radians; `lungeDir`/`edgeN` are angles. */
export interface SnapBean {
  slot: number;
  state: BS; st: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; facing: number;
  dizzy: number; cdShove: number; cdGrab: number; cdLedge: number; cdPebble: number; cdCup: number;
  lungeDir: number; partner: number; escape: number; edgeX: number; edgeY: number; edgeN: number;
  item: ItemKind; charges: number; shakeTicks: number; floatTicks: number; invulnTicks: number;
  potatoImmune: number; jumpBuffer: number; flags: number; place: number; respawnTicks: number;
  crownTicks: number; readyTicks: number;
}
export type BeanFieldName = Exclude<keyof SnapBean, 'slot'>;
type BeanField = FieldDef<Bean, BeanFieldName>;

function beanField(name: BeanFieldName, type: FieldType, scale: number, get: (b: Bean) => number, set: (b: Bean, v: number) => void): BeanField {
  return { name, type, scale, get, set };
}

function beanFlags(b: Bean): number {
  return (b.out ? BeanFlag.OUT : 0) | (b.hasPotato ? BeanFlag.HAS_POTATO : 0) | (b.holdsCrown ? BeanFlag.HOLDS_CROWN : 0) | (b.coyoteTicks > 0 ? BeanFlag.COYOTE : 0);
}

function applyBeanFlags(b: Bean, v: number): void {
  b.out = (v & BeanFlag.OUT) !== 0;
  b.hasPotato = (v & BeanFlag.HAS_POTATO) !== 0;
  b.holdsCrown = (v & BeanFlag.HOLDS_CROWN) !== 0;
  // Only "coyote > 0" is replicated; one remaining tick never predicts a jump the server rejects.
  b.coyoteTicks = (v & BeanFlag.COYOTE) !== 0 ? 1 : 0;
}

export const BEAN_SCHEMA: readonly BeanField[] = [
  beanField('state', 'u8', 1, (b) => b.state, (b, v) => { b.state = v as BS; }),
  beanField('st', 'u16', 1, (b) => b.st, (b, v) => { b.st = v; }),
  beanField('x', 'i16', CM, (b) => b.pos.x, (b, v) => { b.pos.x = v; }),
  beanField('y', 'i16', CM, (b) => b.pos.y, (b, v) => { b.pos.y = v; }),
  beanField('z', 'i16', CM, (b) => b.z, (b, v) => { b.z = v; }),
  beanField('vx', 'i16', CM, (b) => b.vel.x, (b, v) => { b.vel.x = v; }),
  beanField('vy', 'i16', CM, (b) => b.vel.y, (b, v) => { b.vel.y = v; }),
  beanField('vz', 'i16', CM, (b) => b.vz, (b, v) => { b.vz = v; }),
  beanField('facing', 'ang8', 1, (b) => b.facing, (b, v) => { b.facing = v; }),
  beanField('dizzy', 'u8', 1, (b) => b.dizzy, (b, v) => { b.dizzy = v; }),
  beanField('cdShove', 'u8', 1, (b) => b.cdShove, (b, v) => { b.cdShove = v; }),
  beanField('cdGrab', 'u8', 1, (b) => b.cdGrab, (b, v) => { b.cdGrab = v; }),
  beanField('cdLedge', 'u16', 1, (b) => b.cdLedge, (b, v) => { b.cdLedge = v; }),
  beanField('cdPebble', 'u16', 1, (b) => b.cdPebble, (b, v) => { b.cdPebble = v; }),
  beanField('cdCup', 'u8', 1, (b) => b.cdCup, (b, v) => { b.cdCup = v; }),
  beanField('lungeDir', 'ang8', 1, (b) => Math.atan2(b.lungeDir.y, b.lungeDir.x), (b, v) => { b.lungeDir.x = Math.cos(v); b.lungeDir.y = Math.sin(v); }),
  beanField('partner', 'u8', 1, (b) => b.partner, (b, v) => { b.partner = v; }),
  beanField('escape', 'u8', 1, (b) => b.escape, (b, v) => { b.escape = v; }),
  beanField('edgeX', 'i16', CM, (b) => b.edgePoint.x, (b, v) => { b.edgePoint.x = v; }),
  beanField('edgeY', 'i16', CM, (b) => b.edgePoint.y, (b, v) => { b.edgePoint.y = v; }),
  beanField('edgeN', 'ang8', 1, (b) => Math.atan2(b.edgeNormal.y, b.edgeNormal.x), (b, v) => { b.edgeNormal.x = Math.cos(v); b.edgeNormal.y = Math.sin(v); }),
  beanField('item', 'u8', 1, (b) => b.item, (b, v) => { b.item = v as ItemKind; }),
  beanField('charges', 'u8', 1, (b) => b.charges, (b, v) => { b.charges = v; }),
  beanField('shakeTicks', 'u16', 1, (b) => b.shakeTicks, (b, v) => { b.shakeTicks = v; }),
  beanField('floatTicks', 'u16', 1, (b) => b.floatTicks, (b, v) => { b.floatTicks = v; }),
  beanField('invulnTicks', 'u16', 1, (b) => b.invulnTicks, (b, v) => { b.invulnTicks = v; }),
  beanField('potatoImmune', 'u8', 1, (b) => b.potatoImmuneTicks, (b, v) => { b.potatoImmuneTicks = v; }),
  beanField('jumpBuffer', 'u8', 1, (b) => b.jumpBufferTicks, (b, v) => { b.jumpBufferTicks = v; }),
  beanField('flags', 'u8', 1, beanFlags, applyBeanFlags),
  beanField('place', 'u8', 1, (b) => b.place, (b, v) => { b.place = v; }),
  beanField('respawnTicks', 'u16', 1, (b) => b.respawnTicks, (b, v) => { b.respawnTicks = v; }),
  beanField('crownTicks', 'u16', 1, (b) => b.crownTicks, (b, v) => { b.crownTicks = v; }),
  beanField('readyTicks', 'u8', 1, (b) => b.readyTicks, (b, v) => { b.readyTicks = v; }),
];
export const BEAN_RECORD_BYTES = recordBytes(BEAN_SCHEMA);

// ---- ENT record (23 B) -------------------------------------------------------------------------

export interface SnapEnt {
  id: number; kind: EK; owner: number; x: number; y: number; z: number; vx: number; vy: number; vz: number;
  t: number; timer: number; data: number; data2: number;
}
export type EntFieldName = keyof SnapEnt;
type EntField = FieldDef<Entity, EntFieldName>;

function entField(name: EntFieldName, type: FieldType, scale: number, get: (e: Entity) => number, set: (e: Entity, v: number) => void): EntField {
  return { name, type, scale, get, set };
}

export const ENT_SCHEMA: readonly EntField[] = [
  entField('id', 'u16', 1, (e) => e.id, (e, v) => { e.id = v; }),
  entField('kind', 'u8', 1, (e) => e.kind, (e, v) => { e.kind = v as EK; }),
  entField('owner', 'u8', 1, (e) => e.owner, (e, v) => { e.owner = v; }),
  entField('x', 'i16', CM, (e) => e.pos.x, (e, v) => { e.pos.x = v; }),
  entField('y', 'i16', CM, (e) => e.pos.y, (e, v) => { e.pos.y = v; }),
  entField('z', 'i16', CM, (e) => e.z, (e, v) => { e.z = v; }),
  entField('vx', 'i16', CM, (e) => e.vel.x, (e, v) => { e.vel.x = v; }),
  entField('vy', 'i16', CM, (e) => e.vel.y, (e, v) => { e.vel.y = v; }),
  entField('vz', 'i16', CM, (e) => e.vz, (e, v) => { e.vz = v; }),
  entField('t', 'u16', 1, (e) => e.t, (e, v) => { e.t = v; }),
  entField('timer', 'u16', 1, (e) => e.timer, (e, v) => { e.timer = v; }),
  entField('data', 'u8', 1, (e) => e.data, (e, v) => { e.data = v; }),
  entField('data2', 'i16', 1, (e) => e.data2, (e, v) => { e.data2 = v; }),
];
export const ENT_RECORD_BYTES = recordBytes(ENT_SCHEMA);

// ---- MODE block (7 B) --------------------------------------------------------------------------

/** A decoded MODE block. `matchPoint` is 0/1. The potato fuse is never on the wire. */
export interface SnapMode { potatoSlot: number; potatoIntensity: number; crownEntId: number; crownHolder: number; matchPoint: number; aliveAtStart: number }
export type ModeFieldName = keyof SnapMode;
type ModeField = FieldDef<ModeState, ModeFieldName>;

function modeField(name: ModeFieldName, type: FieldType, get: (m: ModeState) => number, set: (m: ModeState, v: number) => void): ModeField {
  return { name, type, scale: 1, get, set };
}

export const MODE_SCHEMA: readonly ModeField[] = [
  modeField('potatoSlot', 'u8', (m) => m.potatoSlot, (m, v) => { m.potatoSlot = v; }),
  modeField('potatoIntensity', 'u8', (m) => m.potatoIntensity, (m, v) => { m.potatoIntensity = v; }),
  modeField('crownEntId', 'u16', (m) => m.crownEntId, (m, v) => { m.crownEntId = v; }),
  modeField('crownHolder', 'u8', (m) => m.crownHolder, (m, v) => { m.crownHolder = v; }),
  modeField('matchPoint', 'u8', (m) => (m.matchPoint ? 1 : 0), (m, v) => { m.matchPoint = v !== 0; }),
  modeField('aliveAtStart', 'u8', (m) => m.aliveAtStart, (m, v) => { m.aliveAtStart = v; }),
];
export const MODE_BLOCK_BYTES = recordBytes(MODE_SCHEMA);

// ---- MAP blocks, EVENT record, headers ---------------------------------------------------------

/** TEACUP: tiltX i16, tiltY i16, wedgeFallen u8, wedgeWarn u8, cupOccupant u8, sdCursor u8, wedgeOrder 8×u8. */
export const TEACUP_BLOCK_BYTES = 2 + 2 + 1 + 1 + 1 + 1 + MAP_WEDGE_COUNT;
/** CAKE: sdCursor u8, tileState 37×u8, tileTimer 37×u16. */
export const CAKE_BLOCK_BYTES = 1 + MAP_TILE_COUNT + 2 * MAP_TILE_COUNT;

export function mapBlockBytes(mapId: MapId): number {
  if (mapId === MapId.TEACUP) return TEACUP_BLOCK_BYTES;
  if (mapId === MapId.CAKE) return CAKE_BLOCK_BYTES;
  return 0;
}

/** type u8, tickBack u8, a u8, b u8, c i16, d i16, x i16 (cm), y i16 (cm). */
export const EVENT_RECORD_BYTES = 12;
/** type u8, roomTick u32, roundId u8, roundTick i32, ackTick u32, inputSlack i8. */
export const SNAPSHOT_HEADER_BYTES = 15;
/** type u8, firstTick u32, viewTick u32, count u8. */
export const INPUT_HEADER_BYTES = 10;
export const INPUT_FRAME_BYTES = 10;
export const INPUT_MAX_FRAMES = 8;
